import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * ErrorBoundary — reusable React error boundary (class component).
 *
 * Mandate B (Resilience Lens): a single component crash otherwise unmounts the
 * whole React tree (White Screen of Death). This boundary captures render-time
 * errors, console-logs them with the component stack, and renders a stylized
 * fallback in place of the crashed subtree — so surrounding UI (Header, Chat,
 * Toaster, timers outside the crashed region) stays mounted and interactive.
 *
 * DESIGN:
 * - One component, many placements: a `fallback` prop is either a ReactNode
 *   (rendered verbatim) or a render-prop `((error, reset) => ReactNode)` for a
 *   custom scoped fallback. When omitted, a tasteful default stylized fallback
 *   is rendered.
 * - `onError` callback lets callers forward errors to structured logging.
 * - `reset()` clears the boundary state; useful when a parent re-renders (e.g.
 *   RehydrationProvider) and the boundary should get a fresh chance. NOTE: for
 *   state-poisoning crashes (bad Zustand/socket payload), a soft re-mount alone
 *   can re-crash in a loop — callers should prefer `window.location.reload()`
 *   to guarantee a clean rehydration. The default fallback does exactly that.
 */

export interface ErrorBoundaryProps {
  children: ReactNode;
  /** A node rendered on error, OR a render-prop receiving (error, reset). */
  fallback?: ReactNode | ((error: Error, reset: () => void) => ReactNode);
  /** Optional structured-logging sink (errors are always console.error'd too). */
  onError?: (error: Error, info: { componentStack: string | null }) => void;
  /** Optional className passthrough for the default fallback card. */
  fallbackClassName?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary] Render crash:', error, info.componentStack);
    this.props.onError?.(error, { componentStack: info.componentStack ?? null });
  }

  reset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    const { hasError, error } = this.state;
    if (!hasError || !error) {
      return this.props.children;
    }

    const { fallback, fallbackClassName } = this.props;
    if (typeof fallback === 'function') {
      return fallback(error, this.reset);
    }
    if (fallback !== undefined) {
      return fallback;
    }
    return <DefaultFallback error={error} reset={this.reset} className={fallbackClassName} />;
  }
}


/** Stylized default fallback: an alert card with a reload affordance. */
function DefaultFallback({
  error,
  reset,
  className,
}: {
  error: Error;
  reset: () => void;
  className?: string | undefined;
}): JSX.Element {
  const handleReload = (): void => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  return (
    <div
      role="alert"
      className={cn(
        'mx-auto flex max-w-md flex-col items-center gap-3 rounded-xl border border-destructive/40 bg-destructive/5 p-6 text-center',
        className,
      )}
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
        <AlertTriangle className="h-6 w-6 text-destructive" aria-hidden="true" />
      </div>
      <h2 className="text-lg font-semibold text-destructive">Something went wrong</h2>
      <p className="text-sm text-muted-foreground">
        An unexpected error broke this part of the interface. Reloading the page
        restores a clean state.
      </p>
      <pre className="mt-1 w-full overflow-x-auto rounded-md bg-muted/60 px-3 py-2 text-left text-xs font-mono text-muted-foreground">
        {error.message || 'Unknown error'}
      </pre>
      <div className="mt-2 flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={reset} aria-label="Try rendering again">
          <RotateCcw className="mr-1.5 h-4 w-4" aria-hidden="true" />
          Try again
        </Button>
        <Button variant="destructive" size="sm" onClick={handleReload} aria-label="Reload the page">
          Reload
        </Button>
      </div>
    </div>
  );
}
